import type { FC, ReactNode } from 'react'
import type { Review } from '@/types'

import { Quote, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/landing/section-heading'

interface ReviewsSectionProps {
    reviews: Array<Review>
}

export const ReviewsSection: FC<ReviewsSectionProps> = ({ reviews }): ReactNode => {
    if (reviews.length === 0) return null

    return (
        <section id='reviews' aria-labelledby='reviews-title' className='scroll-mt-20 py-20 sm:py-28'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    titleId='reviews-title'
                    align='center'
                    eyebrow="Reviews"
                    title={<>Regulars say it <em>best.</em></>}
                    description="A few words from the people who keep coming back for another bowl."
                />

                <ul className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    {reviews.map((review, index) => {
                        const isHighlighted = index % 3 === 1

                        return (
                            <li key={review.id}>
                                <figure
                                    className={cn(
                                        'flex h-full flex-col rounded-[1.75rem] p-7 sm:p-8',
                                        isHighlighted
                                            ? 'bg-primary text-primary-foreground shadow-xl shadow-primary/20'
                                            : 'border border-border bg-card shadow-sm shadow-broth/5'
                                    )}
                                >
                                    <div className='flex items-center justify-between'>
                                        <Quote
                                            aria-hidden='true'
                                            className={cn('size-8', isHighlighted ? 'text-primary-foreground/50' : 'text-primary/40')}
                                        />

                                        {review.rating > 0 && (
                                            <div role='img' aria-label={`Rated ${review.rating} out of 5`} className='flex gap-0.5'>
                                                {Array.from({ length: 5 }, (_, star) => (
                                                    <Star
                                                        key={star}
                                                        aria-hidden='true'
                                                        className={cn(
                                                            'size-4',
                                                            star < review.rating
                                                                ? isHighlighted ? 'fill-yolk text-yolk' : 'fill-primary text-primary'
                                                                : isHighlighted ? 'text-primary-foreground/40' : 'text-border'
                                                        )}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <blockquote className='mt-6 flex-1'>
                                        <p
                                            data-mdlfy-cms={`reviews:${review.id}:quote`}
                                            className='font-heading text-xl leading-snug'
                                        >
                                            {review.quote}
                                        </p>
                                    </blockquote>

                                    <figcaption className='mt-8 flex items-center gap-3'>
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'inline-flex size-11 shrink-0 items-center justify-center rounded-full font-heading text-lg font-semibold',
                                                isHighlighted ? 'bg-primary-foreground/15' : 'bg-yolk-soft text-foreground'
                                            )}
                                        >
                                            {review.authorName.trim().charAt(0).toUpperCase()}
                                        </span>

                                        <span className='min-w-0'>
                                            <span
                                                data-mdlfy-cms={`reviews:${review.id}:author_name`}
                                                className='block font-semibold'
                                            >
                                                {review.authorName}
                                            </span>

                                            {review.authorDetail && (
                                                <span
                                                    data-mdlfy-cms={`reviews:${review.id}:author_detail`}
                                                    className={cn('block text-sm', isHighlighted ? 'text-primary-foreground/75' : 'text-muted-foreground')}
                                                >
                                                    {review.authorDetail}
                                                </span>
                                            )}
                                        </span>
                                    </figcaption>
                                </figure>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}
